import React from 'react';
import {
    Factory, Building, Lightbulb, Truck, ShoppingBag,
    FileText, Shield, HeartPulse, GraduationCap
} from 'lucide-react';

const AI_SOLUTIONS = [
    {
        title: "Manufacturing Defect Detection",
        challenge: "Production line defects costing $2.4M annually in recalls and rework",
        solution: "Computer vision AI system integrated with production lines for real-time quality control",
        result: "92% reduction in defects and $1.8M annual savings in the first year"
    },
    {
        title: "Intelligent Real Estate Portfolio",
        challenge: "Manual property valuation process taking 14 days per assessment, limiting market responsiveness",
        solution: "Automated AI valuation model incorporating 200+ market variables and spatial data",
        result: "Valuation time reduced to 30 minutes with 96% accuracy compared to final sale prices"
    },
    {
        title: "Smart Energy Management",
        challenge: "Complex facility with unpredictable energy consumption patterns and excessive utility costs",
        solution: "IoT-powered AI system predicting usage patterns and automatically optimizing consumption",
        result: "31% reduction in energy costs and 47% decrease in carbon footprint"
    },
    {
        title: "Autonomous Fleet Optimization",
        challenge: "Delivery routes manually planned, causing delays and excess fuel consumption across 50+ vehicles",
        solution: "Dynamic route optimization AI adapting to traffic, weather, and delivery priorities in real-time",
        result: "28% reduction in fuel costs and 35% improvement in on-time deliveries"
    },
    {
        title: "Retail Inventory Intelligence",
        challenge: "Inventory imbalances resulting in $3.2M in markdowns and $1.8M in stockout losses annually",
        solution: "Predictive AI model for inventory management with automated replenishment triggers",
        result: "62% reduction in markdowns and 38% fewer stockouts within six months"
    },
    {
        title: "Document Intelligence System",
        challenge: "Legal team spending 1,800+ hours annually reviewing contracts and identifying compliance issues",
        solution: "NLP-powered contract analysis system with automatic risk flagging and term extraction",
        result: "75% reduction in review time and identification of $1.2M in missed obligations"
    },
    {
        title: "Cybersecurity Threat Prevention",
        challenge: "Security team overwhelmed by 10,000+ daily alerts with critical threats buried in noise",
        solution: "AI-driven threat detection system with behavioral analysis and anomaly prioritization",
        result: "94% reduction in false positives and prevention of two major breach attempts"
    },
    {
        title: "Patient Care Optimization",
        challenge: "Hospital readmission rates 22% above national average, impacting care quality and reimbursements",
        solution: "Predictive patient risk model analyzing 300+ variables to identify intervention opportunities",
        result: "Readmission rates reduced by 38% and patient satisfaction scores increased by 29%"
    },
    {
        title: "Personalized Learning Platform",
        challenge: "One-size-fits-all curriculum leaving 24% of students behind and 18% disengaged",
        solution: "Adaptive learning AI customizing educational content based on individual learning patterns",
        result: "Comprehension improved by 41% and course completion rates increased by 36%"
    }
];

// Get color classes based on index
const getColorClasses = (index, element) => {
    const colors = ["blue", "indigo", "purple", "pink", "amber", "emerald"];
    const color = colors[index % colors.length];

    const colorMap = {
        blue: {
            text: "text-blue-400",
            bgLight: "bg-blue-400/10",
            bgMedium: "bg-blue-500/20",
            bgGradient: "from-blue-600 to-blue-400",
            border: "border-blue-500/30",
            shadow: "shadow-blue-500/30"
        },
        indigo: {
            text: "text-indigo-400",
            bgLight: "bg-indigo-400/10",
            bgMedium: "bg-indigo-500/20",
            bgGradient: "from-indigo-600 to-indigo-400",
            border: "border-indigo-500/30",
            shadow: "shadow-indigo-500/30"
        },
        purple: {
            text: "text-purple-400",
            bgLight: "bg-purple-400/10",
            bgMedium: "bg-purple-500/20",
            bgGradient: "from-purple-600 to-purple-400",
            border: "border-purple-500/30",
            shadow: "shadow-purple-500/30"
        },
        pink: {
            text: "text-pink-400",
            bgLight: "bg-pink-400/10",
            bgMedium: "bg-pink-500/20",
            bgGradient: "from-pink-600 to-pink-400",
            border: "border-pink-500/30",
            shadow: "shadow-pink-500/30"
        },
        amber: {
            text: "text-amber-400",
            bgLight: "bg-amber-400/10",
            bgMedium: "bg-amber-500/20",
            bgGradient: "from-amber-600 to-amber-400",
            border: "border-amber-500/30",
            shadow: "shadow-amber-500/30"
        },
        emerald: {
            text: "text-emerald-400",
            bgLight: "bg-emerald-400/10",
            bgMedium: "bg-emerald-500/20",
            bgGradient: "from-emerald-600 to-emerald-400",
            border: "border-emerald-500/30",
            shadow: "shadow-emerald-500/30"
        }
    };

    return colorMap[color][element];
};

// Function to get the appropriate icon based on index
const getAiSolutionIcon = (index) => {
    const icons = [
        <Factory className="w-10 h-10" />,
        <Building className="w-10 h-10" />,
        <Lightbulb className="w-10 h-10" />,
        <Truck className="w-10 h-10" />,
        <ShoppingBag className="w-10 h-10" />,
        <FileText className="w-10 h-10" />,
        <Shield className="w-10 h-10" />,
        <HeartPulse className="w-10 h-10" />,
        <GraduationCap className="w-10 h-10" />
    ];
    return icons[index % icons.length];
};

// AI Solution Card with pipeline aesthetic
const AiSolutionCard = ({ solution, index }) => (
    <div className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:translate-y-[-8px]">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800/90 to-slate-900/90 backdrop-blur-sm shadow-lg" />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-blue-600/20 transition-opacity duration-500" />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />

        {/* Pipeline connector on left side */}
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-6 h-20 bg-slate-800 rounded-r-md border border-blue-500/30 overflow-hidden">
            <div className="absolute inset-y-0 right-0 w-1 bg-blue-500/30"></div>
            <div className="absolute top-1/2 left-0 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
        </div>

        <div className="relative p-6 flex flex-col h-full">
            <div className={`bg-gradient-to-tr from-slate-800 to-slate-700 p-3 rounded-lg border border-slate-600/30 self-start mb-5`}>
                <div className={getColorClasses(index, "text")}>
                    {getAiSolutionIcon(index)}
                </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-3">{solution.title}</h3>

            <div className="text-slate-300 leading-relaxed space-y-2">
                <p><span className={`${getColorClasses(index, "text")} font-medium`}>Challenge:</span> {solution.challenge}</p>
                <p><span className={`${getColorClasses(index, "text")} font-medium`}>Solution:</span> {solution.solution}</p>
                <p><span className={`${getColorClasses(index, "text")} font-medium`}>Result:</span> {solution.result}</p>
            </div>

            {/* Data flow indicators at bottom */}
            <div className="mt-auto pt-6 w-full">
                <div className="w-full h-1 bg-slate-700/50 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${getColorClasses(index, "bgGradient")} w-0 group-hover:w-full transition-all duration-1000 rounded-full`}></div>
                </div>
                <div className="mt-3 flex justify-between items-center">
                    <div className="flex items-center">
                        <div className={`w-2 h-2 ${getColorClasses(index, "text")} rounded-full mr-1 animate-pulse`}></div>
                        <span className={`text-xs ${getColorClasses(index, "text")}`}>
                            {index === 0 && "Quality Control"}
                            {index === 1 && "Valuation Delay"}
                            {index === 2 && "Energy Waste"}
                            {index === 3 && "Inefficient Routes"}
                            {index === 4 && "Inventory Imbalance"}
                            {index === 5 && "Document Overload"}
                            {index === 6 && "Threat Detection"}
                            {index === 7 && "High Readmissions"}
                            {index === 8 && "Learning Gaps"}
                        </span>
                    </div>
                    <div className="flex items-center">
                        <span className={`text-xs ${getColorClasses(index, "text")}`}>
                            {index === 0 && "AI Vision System"}
                            {index === 1 && "Automated Valuation"}
                            {index === 2 && "Smart Optimization"}
                            {index === 3 && "Dynamic Routing"}
                            {index === 4 && "Predictive Analytics"}
                            {index === 5 && "NLP Analysis"}
                            {index === 6 && "Anomaly Detection"}
                            {index === 7 && "Risk Prediction"}
                            {index === 8 && "Adaptive Learning"}
                        </span>
                        <div className={`w-2 h-2 ${getColorClasses(index, "text")} rounded-full ml-1 animate-pulse`}></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const AiSolutionsSection = () => {
    return (
        <section className="relative z-10 py-24 px-4 overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight">
                        <span className="text-white">DIO+ AI </span>
                        <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-purple-400 bg-clip-text text-transparent">
                            in Action
                        </span>
                    </h2>

                    <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                        Real-world implementations of our intelligent systems delivering measurable business impact
                    </p>
                </div>

                {/* AI Solutions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
                    {AI_SOLUTIONS.map((solution, index) => (
                        <AiSolutionCard
                            key={index}
                            solution={solution}
                            index={index}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AiSolutionsSection;